import React from 'react';
import { DropdownPill } from './dropdown-pill';
import { useTagGroups, TagGroup } from '../hooks/api/useTagGroups';

interface TopicFilterDropdownProps {
  selectedTagGroupId?: number | undefined;
  onSelect: (value: number | undefined) => void;
  label?: string;
  placeholder?: string;
  includeAllOption?: boolean;
  allOptionLabel?: string;
  topicTagGroups?: TagGroup[]; // Optional: if provided, uses this instead of fetching
  className?: string;
}

/**
 * Shared component for topic filter dropdowns with icons.
 * Automatically fetches topic tag groups and maps them to dropdown options with icons.
 */
export const TopicFilterDropdown: React.FC<TopicFilterDropdownProps> = ({
  selectedTagGroupId,
  onSelect,
  label = 'Filter by topic',
  placeholder = 'Filter by topic',
  includeAllOption = false,
  allOptionLabel = 'All Topics',
  topicTagGroups: providedTopicTagGroups,
  className,
}) => {
  const { data: fetchedTopicTagGroups } = useTagGroups();

  const options = React.useMemo(() => {
    const topicTagGroups = providedTopicTagGroups || fetchedTopicTagGroups || [];
    const topicOptions = topicTagGroups.map((tg) => ({
      value: tg.id,
      label: tg.name,
    }));

    if (includeAllOption) {
      return [{ value: undefined, label: allOptionLabel }, ...topicOptions];
    }

    return topicOptions;
  }, [providedTopicTagGroups, fetchedTopicTagGroups, includeAllOption, allOptionLabel]);

  const handleSelect = React.useCallback(
    (value: string | number | undefined) => {
      onSelect(value as number | undefined);
    },
    [onSelect],
  );

  return (
    <DropdownPill
      label={label}
      placeholder={placeholder}
      options={options}
      selectedValue={selectedTagGroupId}
      onSelect={handleSelect}
      className={className}
    />
  );
};
